import { createSlice } from '@reduxjs/toolkit';
import {
    createStudy,
    getAnalysisTypeAccordingToStudyType,
    getClosedSessionInfo,
    getCompletedAnalysis,
    requestStudyAnalysis,
} from '../../services/diagnocat/diagnocat.service';
import UploadService from '../../services/diagnocat/upload-files.service';
import { deleteStudyState, loadPatientStudiesWithImagesAndAnalyses } from './diagnocatSlice';

const uploadInitialState = {
    activeSessions: [],
};

export const uploadSlice = createSlice({
    name: 'upload',
    initialState: uploadInitialState,
    reducers: {
        // standard reducer logic, with auto-generated action types per reducer
        uploadLoading(state, action) {
            let newSession = {
                startTime: action.payload,
                studyId: null,
                sessionId: null,
                loading: 'pending',
                message: 'containers.diagnocat.uploads.openingUploadSession',
                error: null,
            };
            state.activeSessions = state.activeSessions.concat(newSession);
        },
        uploadSucceed(state, action) {
            return {
                ...state,
                activeSessions: state.activeSessions.map((session) =>
                    session.startTime === action.payload
                        ? {
                              ...session,
                              loading: 'idle',
                              message: 'containers.diagnocat.uploads.uploadFinished',
                              endTime: Date.now(),
                          }
                        : session
                ),
            };
        },
        uploadFailed(state, action) {
            return {
                ...state,
                activeSessions: state.activeSessions.map((session) =>
                    session.startTime === action.payload.startTime
                        ? {
                              ...session,
                              error: action.payload.error,
                              loading: 'idle',
                              message: 'containers.diagnocat.uploads.uploadFailed',
                          }
                        : session
                ),
            };
        },
        setLoadingMessage(state, action) {
            return {
                ...state,
                activeSessions: state.activeSessions.map((session) =>
                    session.sessionId === action.payload.sessionId
                        ? { ...session, message: action.payload.message }
                        : session
                ),
            };
        },

        updateSession(state, action) {
            return {
                ...state,
                activeSessions: state.activeSessions.map((session) =>
                    session.startTime === action.payload.startTime
                        ? {
                              ...session,
                              sessionId: action.payload.sessionId,
                              studyId: action.payload.studyId,
                              patient: action.payload.selectedPatient,
                          }
                        : session
                ),
            };
        },

        cleanSessionFromActiveSessions(state, action) {
            state.activeSessions = state.activeSessions.filter(
                (session) => session.startTime !== action.payload
            );
        },
    },
});

// Destructure and export the plain action creators
export const {
    uploadLoading,
    uploadSucceed,
    uploadFailed,
    setLoadingMessage,
    updateSession,
    cleanSessionFromActiveSessions,
} = uploadSlice.actions;

export const uploadFile = (selectedPatient, selectedFileType, selectedFile) => async (dispatch) => {
    const startTime = Date.now();
    const patientId = selectedPatient.id;
    dispatch(uploadLoading(startTime));

    //1. create study
    let studyResponse = await createStudy(patientId, {
        study_type: selectedFileType,
    });
    const studyId = studyResponse.uid;

    try {
        //2. open session
        let openSessionResponse = await UploadService.openSession(patientId, studyId);
        const sessionId = openSessionResponse.data.session_id;

        dispatch(updateSession({ startTime, studyId, sessionId, selectedPatient }));

        //3. request upload urls
        const uploadUrlsRequest = {
            session_id: sessionId,
            keys: [selectedFile.name],
        };

        let requestUploadUrlsResponse = await UploadService.requestUploadUrls(uploadUrlsRequest);

        dispatch(
            setLoadingMessage({ message: 'containers.diagnocat.uploads.uploadingFiles', sessionId })
        );
        //4. upload the files to the cloud
        let cloudStorageUrl = requestUploadUrlsResponse.data.upload_urls[0].url;
        let uploadFilesResponse = await UploadService.uploadFiles(cloudStorageUrl, selectedFile);
        const etag = uploadFilesResponse.headers.etag;

        dispatch(
            setLoadingMessage({
                message: 'containers.diagnocat.uploads.processingFiles',
                sessionId,
            })
        );
        //5. notify progress
        const progressNotifyRequest = {
            session_id: sessionId,
            updates: [{ key: selectedFile.name, etag: etag }],
        };

        let uploadProgressNotifyResponse = await UploadService.uploadProgressNotify(
            progressNotifyRequest
        );

        //6. Start session close:
        const sessionIdRequest = {
            session_id: sessionId,
        };
        let startProcessingResponse = await UploadService.startSessionProcessing(sessionIdRequest);

        //7. Check session info:
        let checkSessionInfoResult = await getClosedSessionInfo(sessionIdRequest);

        //8. After session is closed - Request analysis
        dispatch(
            setLoadingMessage({
                message: 'containers.diagnocat.uploads.processingAnalysis',
                sessionId,
            })
        );
        let requestAnalysisRes = await requestStudyAnalysis(studyId, {
            analysis_type: getAnalysisTypeAccordingToStudyType(selectedFileType),
        });
        let analysisId = requestAnalysisRes.uid;

        //9. Wait for analysis to finish:
        let getAnalysisResult = await getCompletedAnalysis(analysisId);

        dispatch(uploadSucceed(startTime));

        //Clear the finished session from state after 300000 ms = 5 minute
        setTimeout(() => {
            dispatch(cleanSessionFromActiveSessions(startTime));
        }, 300000);
    } catch (error) {
        //if upload fails - delete study and remove session from active sessions(after a timeout, in order to show the error of the session)
        dispatch(deleteStudyState(studyId));
        setTimeout(() => {
            dispatch(cleanSessionFromActiveSessions(startTime));
        }, 300000);
        dispatch(uploadFailed({startTime, error}));
    }
};

export const selectActiveSessions = (state) => state.upload.activeSessions;

export default uploadSlice.reducer;
